import { useState, type ChangeEvent, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { Alert, Button, Input } from "@agentnative/ui";
import { AuthLayout, authInputClass, authButtonClass, authLinkClass, authLinkRowClass } from "../components/AuthLayout";
import { register, fetchCsrfToken } from "../lib/api/auth";
import { apiErrorMessage } from "../lib/api/client";
import { useAuthStore } from "../stores/auth";

// Client-side hint only -- the actual enforced rule lives server-side
// (services/auth/password.go's minPasswordLength = 8). This is not a
// richer complexity policy, just early feedback before a round trip.
const MIN_PASSWORD_LENGTH = 8;

export function RegisterRoute() {
  const navigate = useNavigate();
  const setUser = useAuthStore((s) => s.setUser);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const tooShort = password.length > 0 && password.length < MIN_PASSWORD_LENGTH;

  const mutation = useMutation({
    mutationFn: async () => {
      await fetchCsrfToken();
      return register({ email, password, name });
    },
    onSuccess: (resp) => {
      if (!resp.success || !resp.user) {
        return;
      }
      setUser(resp.user);
      navigate({ to: "/" });
    },
  });

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (tooShort) return;
    mutation.mutate();
  }

  const registerFailed = mutation.isSuccess && !mutation.data.success;

  return (
    <AuthLayout title="Create an account" subtitle="Join the Agent Native ecosystem">
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        {mutation.isError && (
          <Alert variant="danger" title="Registration failed">
            {apiErrorMessage(mutation.error)}
          </Alert>
        )}
        {registerFailed && (
          <Alert variant="danger" title="Registration failed">
            {mutation.data.message}
          </Alert>
        )}
        <Input
          className={authInputClass}
          label="Name"
          id="register-name"
          autoComplete="name"
          required
          value={name}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
        />
        <Input
          className={authInputClass}
          label="Email"
          id="register-email"
          type="email"
          autoComplete="email"
          required
          value={email}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
        />
        <Input
          className={authInputClass}
          label="Password"
          id="register-password"
          type="password"
          autoComplete="new-password"
          required
          value={password}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
          error={tooShort ? `Password must be at least ${MIN_PASSWORD_LENGTH} characters` : undefined}
        />
        <Button type="submit" variant="primary" className={authButtonClass} disabled={mutation.isPending || tooShort}>
          {mutation.isPending ? "Creating account..." : "Create account"}
        </Button>
        <div className={authLinkRowClass}>
          <a href="/login" className={authLinkClass}>
            Already have an account? Sign in
          </a>
        </div>
      </form>
    </AuthLayout>
  );
}
